import { useState } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCcw01, Lock01 } from "@untitledui/icons";
import { EnvRefTable } from "./GeneralPage";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Table } from "@/components/application/table/table";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { useTlsStatus, useTlsRenew, useTlsReload, type TlsActionResult } from "@/lib/tls";
import { formatDateTime } from "@/lib/format";
import { SectionHeader } from "@/components/application/section-headers/section-headers";

/**
 * TLS — stav (zdroj/protokoly/porty/certifikát) + akce renew (ACME) / reload bundle.
 * Konfigurace se mění env proměnnými; tady jen runtime stav a operace.
 */
export function TlsPage() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useTlsStatus();
  const renew = useTlsRenew();
  const reload = useTlsReload();
  const [result, setResult] = useState<TlsActionResult | null>(null);

  const run = async (m: typeof renew) => {
    setResult(null);
    try { setResult(await m.mutateAsync()); } catch { setResult({ success: false, message: t("common.loadError") }); }
  };

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex-1">
            <SectionHeader.Heading>{t("tls.title")}</SectionHeader.Heading>
            <SectionHeader.Subheading>{t("tls.subtitle")}</SectionHeader.Subheading>
          </div>
        </SectionHeader.Group>
      </SectionHeader.Root>

      {isLoading && <LoadingIndicator />}
      {isError && <p className="text-sm text-error-primary">{t("common.loadError")}</p>}

      {data && (
        <>
          {!data.httpsActive && (
            <div className="rounded-xl bg-secondary p-4 text-sm text-secondary ring-1 ring-secondary">
              <p className="font-medium text-primary">{t("tls.enableHintTitle")}</p>
              <p className="mt-1">{t("tls.enableHint")}</p>
            </div>
          )}

          <section className="rounded-xl bg-primary p-5 ring-1 ring-secondary">
            <div className="mb-3 flex items-center gap-2">
              <Lock01 className="size-5 text-tertiary" />
              <h2 className="text-sm font-semibold text-secondary">{t("tls.status")}</h2>
              <Badge type="pill-color" size="sm" color={data.httpsActive ? "success" : "gray"}>
                {data.httpsActive ? t("tls.https") : t("tls.plainHttp")}
              </Badge>
              <Badge type="pill-color" size="sm" color="blue">{data.source.toUpperCase()}</Badge>
            </div>
            <dl className="text-sm">
              <Row label={t("tls.protocols")}>{data.protocols ?? "—"}</Row>
              <Row label={t("tls.listenPort")}>{data.listenPort}</Row>
              <Row label={t("tls.httpPort")}>{data.httpPort}</Row>
              <Row label={t("tls.httpsPublicPort")}>{data.httpsPublicPort ?? "—"}</Row>
              <Row label="HSTS">{data.hsts ? "on" : "off"}</Row>
              <Row label={t("tls.allowHttp")}>{data.allowHttp ? "on" : "off"}</Row>
              <Row label={t("tls.redirectHttp")}>{data.redirectHttp ? "on" : "off"}</Row>
              <Row label={t("tls.forwardHeaders")}>{data.forwardHeaders}</Row>
              {data.keystorePath && <Row label={t("tls.keystore")}>{data.keystorePath} ({data.keystoreType})</Row>}
              {data.acmeEnabled && <Row label={t("tls.acmeDomains")}>{data.acmeDomains.join(", ") || "—"}</Row>}
              {data.acmeEnabled && <Row label={t("tls.renewalDays")}>{data.renewalDays}</Row>}
            </dl>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button size="sm" color="secondary" iconLeading={RefreshCcw01} isLoading={reload.isPending} onPress={() => void run(reload)}>
                {t("tls.reload")}
              </Button>
              {data.acmeEnabled && (
                <Button size="sm" color="secondary" isLoading={renew.isPending} onPress={() => void run(renew)}>
                  {t("tls.renew")}
                </Button>
              )}
              {result && (
                <span className={`text-sm ${result.success ? "text-success-primary" : "text-error-primary"}`}>
                  {result.success ? "✓ " : "✗ "}{result.message}
                </span>
              )}
            </div>
          </section>

          {data.active && (
            <section className="rounded-xl bg-primary p-5 ring-1 ring-secondary">
              <h2 className="mb-3 text-sm font-semibold text-secondary">{t("tls.activeCert")}</h2>
              <dl className="text-sm">
                <Row label={t("tls.subject")}>{data.active.subject}</Row>
                <Row label={t("tls.issuer")}>{data.active.issuer}</Row>
                <Row label={t("tls.validity")}>{formatDateTime(data.active.notBefore)} → {formatDateTime(data.active.notAfter)}</Row>
                <Row label={t("tls.serial")}>{data.active.serial}</Row>
                <Row label={t("tls.fingerprint")}><span className="font-mono break-all">{data.active.fingerprint}</span></Row>
              </dl>
            </section>
          )}

          {data.history.length > 0 && (
            <section className="rounded-xl bg-primary p-5 ring-1 ring-secondary">
              <h2 className="mb-3 text-sm font-semibold text-secondary">{t("tls.history")}</h2>
              <div className="overflow-x-auto rounded-lg ring-1 ring-secondary">
                <Table aria-label={t("tls.history")} selectionMode="none">
                  <Table.Header>
                    <Table.Head isRowHeader id="installedAt" label={t("tls.installed")} />
                    <Table.Head id="subject" label={t("tls.subject")} />
                    <Table.Head id="notAfter" label={t("tls.expires")} />
                    <Table.Head id="source" label={t("tls.source")} />
                    <Table.Head id="notes" label={t("tls.notes")} />
                    <Table.Head id="active" label={t("tls.activeCol")} />
                  </Table.Header>
                  <Table.Body items={data.history.map((c, i) => ({ ...c, _k: i }))}>
                    {(row) => (
                      <Table.Row id={String(row._k)}>
                        <Table.Cell>{formatDateTime(row.installedAt)}</Table.Cell>
                        <Table.Cell className="max-w-80 truncate">{row.subject}</Table.Cell>
                        <Table.Cell>{formatDateTime(row.notAfter)}</Table.Cell>
                        <Table.Cell>{row.source}</Table.Cell>
                        <Table.Cell className="max-w-60 truncate text-tertiary">{row.notes ?? "—"}</Table.Cell>
                        <Table.Cell>{row.active ? "✓" : ""}</Table.Cell>
                      </Table.Row>
                    )}
                  </Table.Body>
                </Table>
              </div>
            </section>
          )}
        </>
      )}

      <EnvRefTable
        title={t("tls.envReference")}
        rows={[
          ["ETTS_TLS_SOURCE", "none", t("tls.env.source")],
          ["ETTS_TLS_HSTS", "false", t("tls.env.hsts")],
          ["ETTS_TLS_HTTPS_PUBLIC_PORT", "8443", t("tls.env.httpsPort")],
          ["ETTS_TLS_ALLOW_HTTP", "true", t("tls.env.allowHttp")],
          ["SERVER_FORWARD_HEADERS_STRATEGY", "none", t("tls.env.forwardHeaders")],
        ]}
        example={`# Scénář 1 — přímé TLS (keystore v aplikaci)\nETTS_TLS_SOURCE=keystore\nETTS_TLS_HSTS=true\nETTS_TLS_ALLOW_HTTP=false\n\n# Scénář 2 — za reverzní proxy (TLS terminuje proxy, app běží HTTP)\nETTS_TLS_SOURCE=none\nSERVER_FORWARD_HEADERS_STRATEGY=framework`}
      />
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-1 border-b border-secondary py-2 sm:grid-cols-[220px_1fr] sm:gap-4">
      <dt className="text-tertiary">{label}</dt>
      <dd className="break-words text-primary">{children}</dd>
    </div>
  );
}
